<template>
    <v-container>
        <statistical-header :title="title"></statistical-header>
        <loader v-if="loading" />
        <template v-if="!loading">
            <chart id="timeline" :chartData="timelineChartData"/>   
        </template>
    </v-container>
</template>
<script>
import Loader from '@/components/Loader.vue';
import StatisticalHeader from './StatisticalHeader.vue';
import Chart from './Chart.vue';
import axios from 'axios';
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;
import orderBy from 'lodash.orderby';

export default {
    created() {
        this.initialiseItems();
    },
    components: {
        Loader,
        StatisticalHeader,
        Chart
    },
    methods: {
        initialiseItems() {
            this.loading = true;
            const url = `StatisticalInformation/PrimeMinisters`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data;
                    this.loading = false;
                });
        },
    },
    computed: {
            timelineChartData() {
                const generateData = (items, y) => {
                    const data = orderBy(
                        items
                        .map(i => ({...i, PeriodOfService: i.PeriodOfService.map(p => ({...p, Parliamentarian: i.DisplayName, Phid: i.Phid}))}))
                        .flatMap(i => i.PeriodOfService),
                        [p => new Date(p.DateStart).valueOf()],
                        ['desc']
                        )
                        .map((p, i) => {
                            return {
                                name: p.Parliamentarian,
                                color: p.PartyColour,
                                phid: p.Phid,
                                party: p.PartyName,
                                x: new Date(p.DateStart).valueOf(),
                                present: p.DateEnd === null,
                                x2: p.DateEnd ? new Date(p.DateEnd).valueOf() : new Date().valueOf(),
                                y,
                                alignment: i % 2 === 1 ? 'right': 'left',
                                dataLabels: {
                                    align: i % 2 === 1 ? 'right': 'left',
                                    format: i % 2 === 1 ? 
                                    '<a href="/Parliamentarian/{point.phid}" style="text-decoration: none; color: black;margin-right: 30px; display: flex; align-items: center;"><div class="mr-1">{point.name} <span style="color: grey">({point.party})</span></div><img src="https://www.aph.gov.au/api/parliamentarian/{point.phid}/image" style="width: 20px;border-radius: 5px;"></img></a>'
                                    :
                                    '<a href="/Parliamentarian/{point.phid}" style="text-decoration: none; color: black;margin-left: 30px; display: flex; align-items: center;"><img src="https://www.aph.gov.au/api/parliamentarian/{point.phid}/image" style="width: 20px;border-radius: 5px;"></img><div class="ml-1">{point.name} <span style="color: grey">({point.party})</span></div></a>',
                                }
                            }
                        });
                        // need to break up terms of service and get colour
                        let newData = [];
                        data.forEach(d => {
                            // find parliamentarian
                            // find party service
                            const item = items.find(i => i.Phid === d.phid);
                            // replace item with party service
                            // if there is only one party service, don't worry about it - assign colour
                            if (item.Party.length === 1) {
                                d.color = item.Party[0].Colour;
                                d.party = item.Party[0].Name
                                newData.push(d);
                            } else {
                                // need to break record up into segments
                                // for each party
                                // find matching parties
                                item.Party.filter(p => (new Date(p.DateStart).valueOf() < d.x2 && new Date(p.DateEnd).valueOf() > d.x)).forEach(p => {
                                    // find fully matching segment
                                    if (d.x >= new Date(p.DateStart).valueOf() && d.x2 <= new Date(p.DateEnd).valueOf() ) {
                                        d.color = p.Colour;
                                        d.party = p.Name
                                        newData.push(d);
                                    } else {
                                        const newSegment = {
                                            ...d,
                                            x: new Date(p.DateStart).valueOf() > d.x ? new Date(p.DateStart).valueOf() : d.x,
                                            x2: p.DateEnd && new Date(p.DateEnd).valueOf() < d.x2 ? new Date(p.DateEnd).valueOf() : d.x2, 
                                            color: p.Colour,
                                            party: p.Name
                                        };
                                        newData.push(newSegment);
                                    }
                                });
                            }
                        });
                        newData = orderBy(newData, ['x'], ['asc']).map((p, i) => ({
                                ...p,
                                alignment: i % 2 === 1 ? 'right': 'left',
                                dataLabels: {
                                    align: i % 2 === 1 ? 'right': 'left',
                                    format: i % 2 === 1 ? 
                                    '<a href="/Parliamentarian/{point.phid}" class="timeline-label"><div class="mr-1">{point.name} <span class="timeline-label-span">({point.party})</span></div><img src="https://www.aph.gov.au/api/parliamentarian/{point.phid}/image" style="width: 20px;border-radius: 5px;"></img></a>'
                                    :
                                    '<a href="/Parliamentarian/{point.phid}" class="timeline-label-alt" ><img src="https://www.aph.gov.au/api/parliamentarian/{point.phid}/image" style="width: 20px;border-radius: 5px;"></img><div class="ml-1">{point.name} <span class="timeline-label-span">({point.party})</span></div></a>',
                                }
                            }));
                        return newData;
                }
                const data = generateData(this.items, 0);

                const plotLines = this.$store.getters.GET_PARLIAMENTS_ORDERED
                    .map(p => ({
                        value: (new Date(p.DateElection)).valueOf(),
                        dashStyle: 'shortdash',
                        color: '#CCD6EB',
                        label: {
                            text: p.Name,
                            align: 'right',
                            x: -5,
                            y: -7
                        }}));

                return {
                    chart: {
                        type: 'xrange',
                        height: 3000,
                        inverted: true
                    },
                    tooltip: {
                        positioner: function(labelWidth, _, point) {
                            return this.chart.hoverPoint.options.alignment === 'left' ?
                            {
                                x: point.plotX - labelWidth + 20,
                                y: point.plotY - 55 - this.chart.hoverPoint.shapeArgs.width,
                            }
                            :
                            {
                                x: point.plotX + 55,
                                y: point.plotY - 55 - this.chart.hoverPoint.shapeArgs.width,
                            };
                        },                        
                        backgroundColor: 'rgba(255, 255, 255, 0)',
                        shadow: false,
                        borderWidth: 0,
                        borderRadius: 0,
                        useHTML: true,
                        formatter: function () {
                            var todaysDate = new Date();
                            // console.log(this.point)
                            var dateIsToday = new Date(this.x2).setHours(0,0,0,0) == todaysDate.setHours(0,0,0,0);
                            var showEndDate = dateIsToday ? 'present' : new Date(this.x2).toLocaleDateString('en-AU');
                            var tooltipMarkup = '<div class="tooltip" style="width:300px;min-height: 100px;">'
                            + '<div style="display:inline-block;"><img src="https://www.aph.gov.au/api/parliamentarian/' 
                            + this.point.phid 
                            + '/image" style="width: 80px;border-radius: 5px;" /></div><div style="vertical-align: top;display:inline-block; width:200px; padding-left:10px;">' 
                        
                            + '<p style="font-size:1.1em;font-weight:bold;white-space: normal;">' + this.point.name + '</p>' 
                            +  (this.point.phid != null ?  '<p style="white-space: normal; margin-bottom:0.2rem;">' + this.point.party + '</p>' : '')
                            + '<p style="white-space: normal; margin-bottom:0.2rem;">' + new Date(this.x).toLocaleDateString('en-AU') + ' - ' + showEndDate + '</p>'
                            + '</div>'
                            + '</div>';
                            return tooltipMarkup;
                        }, 
                    },
                    title: {
                        text: ''
                    },
                    accessibility: {
                        point: {
                            descriptionFormatter: function (point) {
                                var ix = point.index + 1,
                                    category = point.yCategory,
                                    from = new Date(point.x),
                                    to = new Date(point.x2);
                                return ix + '. ' + category + ', ' + from.toDateString() +
                                    ' to ' + to.toDateString() + '.';
                            }
                        }
                    },
                    plotOptions: {
                        series: {
                            borderWidth: 2,
                            borderColor: 'white',
                            dataLabels: {
                                crop: false,
                                allowOverlap: true
                            }
                        }
                    },
                    xAxis: {
                        type: 'datetime',
                        reversed: false,
                        plotLines
                    },
                    yAxis: {
                        title: {
                            text: ''
                        },
                        categories: [''],
                        reversed: true
                    },
                    legend: {
                        enabled: false
                    },
                    exporting: {
                        enabled: false,
                        sourceWidth: 1000
                    },
                    series: [{
                        name: 'Prime Ministers',
                        pointWidth: 30,
                        data,
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            crop: false
                        }
                    },
                ]                                 
            }
        }
    },    
    data() {
        return {
        title: 'Prime Ministers timeline',
        items: [],
        loading: true,
        }
    }
}
</script>
<style scoped>
    a {
        text-decoration: none;
    }

</style>
<style>
    /* .highcharts-tooltip span {
        background-color: white;
        padding: 0;
        opacity: 1;
        z-index: 9999 !important;
        border-radius: 3px;
        padding: 5px;
        border: 1px solid black;
    } */
</style>
<style lang="stylus" scoped>

    #timeline >>> div {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-container {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-container svg {
        overflow: visible !important;
    }

    #timeline >>> .highcharts-tooltip span {
        background-color: transparent;
    }

    #timeline >>> .highcharts-yaxis-labels text {
        cursor: pointer !important;
    }

    #timeline >>> .timeline-label {
        text-decoration: none; 
        color: black;
        margin-right: 30px; 
        margin-left: 30px; 
        display: flex;
        align-items: center;
    }

    #timeline >>> .timeline-label-alt {
        text-decoration: none; 
        color: black;
        margin-left: 30px; 
        margin-right: 30px; 
        display: flex; 
        align-items: center;
    }

    #timeline >>> .timeline-label-span{
        color: gray;
    }
</style>